import { ArrowRight, Bold, Circle, Eraser, Eye, EyeOff, Italic, MousePointer2, Type } from 'lucide-react';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { Ref } from 'react';

import {
    AnnotationInteractionHandler,
    AnnotationLayer,
    AnnotationType,
    TextEditOverlay,
    compositeImageWithAnnotations,
    hydrateAnnotations,
    serialiseAnnotations,
} from '@/components/snapshot/annotation';
import type {
    AnnotationData,
    AnnotationStyle,
    AnnotationToolMode,
    TextAnnotationData,
    TextEditOverlayHandle,
} from '@/components/snapshot/annotation';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useSnapshotAnnotations } from '@/hooks/use-snapshot-annotations';

// ============================================================================
// Tool button definitions
// ============================================================================

const TOOLS: Array<{ mode: AnnotationToolMode; label: string; Icon: React.ElementType }> = [
    { mode: 'select', label: 'Select', Icon: MousePointer2 },
    { mode: 'arrow', label: 'Arrow', Icon: ArrowRight },
    { mode: 'circle', label: 'Ellipse', Icon: Circle },
    { mode: 'text', label: 'Text', Icon: Type },
    { mode: 'eraser', label: 'Eraser', Icon: Eraser },
];

// ============================================================================
// Component
// ============================================================================

/** In-place text edit session; text is drafted in the DOM overlay until commit. */
interface TextEditSession {
    id: string;
    isNew: boolean;
    /** Text to restore when the edit of an existing annotation is cancelled. */
    originalText: string;
}

export interface SnapshotAnnotationWorkspaceHandle {
    /** Force-commit any open in-place text edit and return the annotations. */
    getAnnotations(): AnnotationData[];
    /**
     * Force-commit any open text edit, then bake the annotations onto the
     * displayed image. `compositedDataUrl` is null when there is nothing to bake.
     */
    bake(): { annotations: AnnotationData[]; compositedDataUrl: string | null };
}

interface SnapshotAnnotationWorkspaceProps {
    /** The image data URL to annotate (with or without overlays). */
    imageUrl: string;
    initialAnnotations?: AnnotationData[];
    /** Live sync of the annotation set (placement, move, style, delete). */
    onAnnotationsChange?: (annotations: AnnotationData[]) => void;
    /** Extra controls appended to the sidebar's Display section (e.g. the Overlays toggle). */
    sidebarExtras?: React.ReactNode;
    ref?: Ref<SnapshotAnnotationWorkspaceHandle>;
}

/**
 * Windowless annotation surface: a left sidebar (tools, display toggles, style
 * options) next to the image + canvas overlay. Text annotations are edited
 * directly on the image via {@link TextEditOverlay} — there is no detached
 * text input.
 *
 * Hosted by the capture wizard and the Screenshots panel's edit mode; the host
 * owns the surrounding window, description field, and save/back actions.
 */
export function SnapshotAnnotationWorkspace({
    imageUrl,
    initialAnnotations,
    onAnnotationsChange,
    sidebarExtras,
    ref,
}: SnapshotAnnotationWorkspaceProps) {
    const stageRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const layerRef = useRef<AnnotationLayer | null>(null);
    const handlerRef = useRef<AnnotationInteractionHandler | null>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const overlayHandleRef = useRef<TextEditOverlayHandle | null>(null);

    const [activeTool, setActiveTool] = useState<AnnotationToolMode>('select');
    const [annotationsVisible, setAnnotationsVisible] = useState(true);
    const [aspectRatio, setAspectRatio] = useState<number | null>(null);
    const [stageSize, setStageSize] = useState<{ width: number; height: number } | null>(null);
    const [textEditSession, setTextEditSession] = useState<TextEditSession | null>(null);

    const {
        annotations,
        selectedId,
        selectedAnnotation,
        setSelectedId,
        setAnnotations,
        updateAnnotationStyle,
    } = useSnapshotAnnotations();

    // ── Sync annotations from layer → hook state ──
    const syncFromLayer = useCallback(() => {
        const layer = layerRef.current;
        if (!layer) return;
        const data = serialiseAnnotations(layer);
        setAnnotations(data);
    }, [setAnnotations]);

    // ── Live sync to the host ──
    const onAnnotationsChangeRef = useRef(onAnnotationsChange);
    onAnnotationsChangeRef.current = onAnnotationsChange;
    useEffect(() => {
        onAnnotationsChangeRef.current?.(annotations);
    }, [annotations]);

    // ── In-place text editing: commit / cancel ──

    /** Force-commit the open text edit session, if any (synchronous). */
    const commitOpenTextEdit = useCallback(() => {
        overlayHandleRef.current?.commit();
    }, []);

    const commitTextEdit = useCallback(
        (id: string, text: string) => {
            const layer = layerRef.current;
            if (!layer) return;
            if (text.trim().length === 0) {
                // Never keep invisible annotations: empty text renders as nothing
                // but would still persist and sync to the server.
                layer.removeObject(id);
                handlerRef.current?.setSelectedId(null);
                setSelectedId(null);
                layer.render();
            } else {
                layer.setAnnotationText(id, text);
                layer.setTextEditing(id, false);
            }
            syncFromLayer();
            setTextEditSession(null);
        },
        [setSelectedId, syncFromLayer]
    );

    const cancelTextEdit = useCallback(
        (id: string, session: TextEditSession) => {
            const layer = layerRef.current;
            if (!layer) return;
            if (session.isNew) {
                layer.removeObject(id);
                handlerRef.current?.setSelectedId(null);
                setSelectedId(null);
                layer.render();
            } else {
                layer.setAnnotationText(id, session.originalText);
                layer.setTextEditing(id, false);
            }
            syncFromLayer();
            setTextEditSession(null);
        },
        [setSelectedId, syncFromLayer]
    );

    // ── Initialise layer + handler once container is mounted ──
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const layer = new AnnotationLayer(container);
        layerRef.current = layer;

        const handler = new AnnotationInteractionHandler(layer);
        handlerRef.current = handler;

        handler.setOnChange(() => {
            syncFromLayer();
            setSelectedId(handler.getSelectedId());
        });

        handler.setOnTextEditRequest(({ id, isNew }) => {
            // Pointerdown fires before the overlay's blur: close any open
            // session deterministically before starting the next one.
            overlayHandleRef.current?.commit();
            layer.setTextEditing(id, true);
            syncFromLayer();
            setSelectedId(id);
            setTextEditSession({
                id,
                isNew,
                originalText: layer.getTextAnnotationText(id) ?? '',
            });
        });

        handler.setOnToolModeChange((mode) => {
            setActiveTool(mode);
        });

        // Size the layer to the image's natural resolution once loaded
        const img = imageRef.current;
        if (img && img.complete && img.naturalWidth > 0) {
            layer.setImageSize(img.naturalWidth, img.naturalHeight);
        }

        // Hydrate initial annotations
        if (initialAnnotations && initialAnnotations.length > 0) {
            hydrateAnnotations(layer, initialAnnotations);
            layer.render();
            syncFromLayer();
        }

        return () => {
            handler.dispose();
            layer.dispose();
            layerRef.current = null;
            handlerRef.current = null;
        };
    }, []);

    // ── Size the layer to the image's natural resolution when it loads ──
    const handleImageLoad = useCallback(() => {
        const img = imageRef.current;
        const layer = layerRef.current;
        if (!img || !layer) return;
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            setAspectRatio(img.naturalWidth / img.naturalHeight);
            layer.setImageSize(img.naturalWidth, img.naturalHeight);
        }
    }, []);

    // ── Track the space available for the image ──
    useEffect(() => {
        const stage = stageRef.current;
        if (!stage) return;
        const observer = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect;
            setStageSize({ width, height });
        });
        observer.observe(stage);
        return () => observer.disconnect();
    }, []);

    // ── Tool mode sync ──
    useEffect(() => {
        handlerRef.current?.setToolMode(activeTool);
    }, [activeTool]);

    // ── Annotation visibility ──
    useEffect(() => {
        layerRef.current?.setAllVisible(annotationsVisible);
    }, [annotationsVisible]);

    // ── Selection sync: handler → hook ──
    useEffect(() => {
        handlerRef.current?.setSelectedId(selectedId);
    }, [selectedId]);

    // ── Click-away commit ──
    // The annotation canvas suppresses focus changes (see AnnotationLayer), so
    // clicking it never blurs the in-place editor. Commit explicitly on any
    // pointerdown outside the editor, in capture phase so the commit lands
    // before the canvas handler processes the click (e.g. an eraser click can
    // hit-test the just-committed text).
    useEffect(() => {
        if (!textEditSession) return;
        const container = containerRef.current;
        if (!container) return;
        const commitOnOutsidePointerDown = (e: PointerEvent) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest('[data-annotation-text-editor]')) return;
            overlayHandleRef.current?.commit();
        };
        container.addEventListener('pointerdown', commitOnOutsidePointerDown, true);
        return () => container.removeEventListener('pointerdown', commitOnOutsidePointerDown, true);
    }, [textEditSession]);

    // ── Style update: propagate hook state → layer objects ──
    const handleStyleChange = useCallback(
        (patch: Partial<AnnotationStyle>) => {
            if (!selectedId) return;
            updateAnnotationStyle(selectedId, patch);

            // Also update the live object in the layer
            const obj = layerRef.current?.getObject(selectedId);
            if (obj) {
                obj.updateStyle(patch);
                layerRef.current?.render();
            }
        },
        [selectedId, updateAnnotationStyle]
    );

    // ── Host-facing handle: commit-then-read and commit-then-bake ──
    useImperativeHandle(
        ref,
        () => ({
            getAnnotations: () => {
                commitOpenTextEdit();
                const layer = layerRef.current;
                return layer ? serialiseAnnotations(layer) : [];
            },
            bake: () => {
                commitOpenTextEdit();
                const img = imageRef.current;
                const layer = layerRef.current;
                if (!img || !layer) return { annotations: [], compositedDataUrl: null };

                const current = serialiseAnnotations(layer);

                // Null when there is nothing to bake: the caller keeps displaying
                // the native base image and clears any previously cached composite.
                let compositedDataUrl: string | null = null;
                if (current.length > 0) {
                    // Make all annotations visible for the composite, and strip
                    // selection/hover handles so they aren't baked into the image.
                    layer.setAllVisible(true);
                    layer.clearDecorations();
                    layer.render();
                    compositedDataUrl = compositeImageWithAnnotations(img, layer.getCanvas());
                }
                return { annotations: current, compositedDataUrl };
            },
        }),
        [commitOpenTextEdit]
    );

    // The image box is measured rather than expressed in CSS: the annotation
    // canvas is absolutely positioned over this exact box (and pointer
    // coordinates are mapped through it), so it must match the rendered image
    // to the pixel — `aspect-ratio` + `max-width` would distort a non-replaced
    // element instead of preserving the ratio. Scales up as well as down so the
    // capture always fills the available height.
    const fittedBox =
        aspectRatio !== null && stageSize !== null && stageSize.width > 0 && stageSize.height > 0
            ? (() => {
                  const width = Math.min(stageSize.width, stageSize.height * aspectRatio);
                  return { width, height: width / aspectRatio };
              })()
            : null;

    const editingAnnotation =
        textEditSession !== null
            ? (annotations.find((a) => a.id === textEditSession.id && a.type === AnnotationType.TEXT) as
                  | TextAnnotationData
                  | undefined)
            : undefined;

    return (
        <div className="flex min-h-0 flex-1">
            {/* Left sidebar: tools, display toggles, and style options */}
            <div className="flex w-36 shrink-0 flex-col overflow-y-auto border-r border-border sm:w-52">
                <div className="flex flex-col gap-0.5 p-2">
                    <p className="px-1 pb-1 text-xs font-medium text-muted-foreground">Tools</p>
                    {TOOLS.map(({ mode, label, Icon }) => (
                        <Button
                            key={mode}
                            variant={activeTool === mode ? 'secondary' : 'ghost'}
                            size="sm"
                            className="w-full justify-start gap-2 text-xs"
                            aria-pressed={activeTool === mode}
                            onClick={() => {
                                commitOpenTextEdit();
                                setActiveTool(mode);
                            }}
                        >
                            <Icon className="h-4 w-4" />
                            {label}
                        </Button>
                    ))}
                </div>

                <div className="flex flex-col gap-0.5 border-t border-border p-2">
                    <p className="px-1 pb-1 text-xs font-medium text-muted-foreground">Display</p>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start gap-2 text-xs"
                        onClick={() => {
                            commitOpenTextEdit();
                            setAnnotationsVisible((v) => !v);
                        }}
                    >
                        {annotationsVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        Annotations
                    </Button>
                    {sidebarExtras}
                </div>

                <div className="flex flex-col gap-2 border-t border-border p-2">
                    <p className="px-1 text-xs font-medium text-muted-foreground">Style</p>
                    {selectedAnnotation ? (
                        <>
                            {/* Color */}
                            <div className="flex items-center justify-between px-1">
                                <Label className="text-xs">Color</Label>
                                <input
                                    type="color"
                                    value={selectedAnnotation.style.color}
                                    onChange={(e) => handleStyleChange({ color: e.target.value })}
                                    className="h-7 w-7 cursor-pointer rounded border border-border"
                                />
                            </div>

                            {/* Line width — not applicable to text annotations */}
                            {selectedAnnotation.type !== AnnotationType.TEXT && (
                                <div className="flex items-center gap-2 px-1">
                                    <Label className="text-xs">Width</Label>
                                    <Slider
                                        min={1}
                                        max={10}
                                        step={1}
                                        value={[selectedAnnotation.style.lineWidth]}
                                        onValueChange={([v]) => handleStyleChange({ lineWidth: v })}
                                        className="flex-1"
                                    />
                                </div>
                            )}

                            {/* Text-specific controls — the text itself is edited in
                                place on the image, not here. */}
                            {selectedAnnotation.type === AnnotationType.TEXT && (
                                <TextControls
                                    annotation={selectedAnnotation}
                                    onStyleChange={handleStyleChange}
                                />
                            )}
                        </>
                    ) : (
                        <p className="px-1 text-xs text-muted-foreground">
                            Select an annotation to edit its style. Double-click a text annotation to edit its
                            text in place.
                        </p>
                    )}
                </div>
            </div>

            {/* Canvas area */}
            <div
                ref={stageRef}
                className="flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-muted/30 p-3"
            >
                <div
                    ref={containerRef}
                    className="relative max-h-full max-w-full"
                    style={fittedBox ?? (aspectRatio ? { aspectRatio } : undefined)}
                >
                    <img
                        ref={imageRef}
                        src={imageUrl}
                        alt="Snapshot"
                        className="block h-full w-full object-contain"
                        onLoad={handleImageLoad}
                        draggable={false}
                    />
                    {textEditSession && editingAnnotation && layerRef.current && (
                        <TextEditOverlay
                            key={textEditSession.id}
                            ref={overlayHandleRef}
                            layer={layerRef.current}
                            annotation={editingAnnotation}
                            isNew={textEditSession.isNew}
                            onCommit={commitTextEdit}
                            onCancel={(id) => cancelTextEdit(id, textEditSession)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// Text-specific style controls
// ============================================================================

interface TextControlsProps {
    annotation: TextAnnotationData;
    onStyleChange: (patch: Partial<AnnotationStyle>) => void;
}

function TextControls({ annotation, onStyleChange }: TextControlsProps) {
    return (
        <>
            <div className="flex items-center gap-2 px-1">
                <Label className="text-xs">Size</Label>
                <Slider
                    min={10}
                    max={48}
                    step={1}
                    value={[annotation.style.fontSize]}
                    onValueChange={([v]) => onStyleChange({ fontSize: v })}
                    className="flex-1"
                />
            </div>

            <div className="flex items-center gap-1 px-1">
                <Button
                    variant={annotation.style.bold ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 w-7 p-0"
                    aria-pressed={annotation.style.bold}
                    onClick={() => onStyleChange({ bold: !annotation.style.bold })}
                >
                    <Bold className="h-3.5 w-3.5" />
                </Button>
                <Button
                    variant={annotation.style.italic ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 w-7 p-0"
                    aria-pressed={annotation.style.italic}
                    onClick={() => onStyleChange({ italic: !annotation.style.italic })}
                >
                    <Italic className="h-3.5 w-3.5" />
                </Button>
            </div>
        </>
    );
}
